import { SENTIMENT_WORD_CONFIGS, SentimentWordConfig } from './config';

export type KeywordSentiment = 'positive' | 'negative' | 'neutral';

export const DEFAULT_SENTIMENT_LOCALE = 'en';

type NormalisedName = {
    tokens: string[];
};

const getLanguageCode = (locale: string): string => locale.toLowerCase().split('-')[0];

export const getSentimentWordConfig = (locale: string = DEFAULT_SENTIMENT_LOCALE): SentimentWordConfig => {
    const normalizedLocale = locale.toLowerCase();

    if (SENTIMENT_WORD_CONFIGS[normalizedLocale]) return SENTIMENT_WORD_CONFIGS[normalizedLocale];

    const languageCode = getLanguageCode(normalizedLocale);

    if (SENTIMENT_WORD_CONFIGS[languageCode]) return SENTIMENT_WORD_CONFIGS[languageCode];

    return SENTIMENT_WORD_CONFIGS[DEFAULT_SENTIMENT_LOCALE];
};

const normaliseName = (name: string): NormalisedName => {
    const normalised = name
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .replace(/[_-]+/g, ' ')
        .replace(/[^\p{Letter}\p{Number}\s]/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const tokens = normalised.split(' ').filter(Boolean).slice(0, 12);

    return { tokens };
};

const isLatinPlural = (token: string): boolean => /s$/u.test(token) && /^\p{Script=Latin}+$/u.test(token);

const getTokenVariants = (token: string): string[] => {
    if (!isLatinPlural(token)) return [token];

    if (token.endsWith('ies')) return [token, `${token.slice(0, -3)}y`];
    if (token.endsWith('es')) return [token, token.slice(0, -2), token.slice(0, -1)];

    return [token, token.slice(0, -1)];
};

const hasMatchedWord = (tokens: string[], words: Set<string>): boolean =>
    tokens.some((token) => getTokenVariants(token).some((tokenVariant) => words.has(tokenVariant)));

export const getKeywordSentiment = (
    name: string | null | undefined,
    locale: string = DEFAULT_SENTIMENT_LOCALE,
): KeywordSentiment => {
    if (!name) return 'neutral';

    const sentimentWordConfig = getSentimentWordConfig(locale);
    const { tokens } = normaliseName(name);
    const { positiveWords, negativeWords } = sentimentWordConfig;

    const hasPositiveMatch = hasMatchedWord(tokens, positiveWords);
    const hasNegativeMatch = hasMatchedWord(tokens, negativeWords);

    if (hasPositiveMatch && hasNegativeMatch) return 'neutral';
    if (hasPositiveMatch) return 'positive';
    if (hasNegativeMatch) return 'negative';

    return 'neutral';
};
